'use client'

import * as React from 'react'
import { Bar, BarChart, CartesianGrid, XAxis, YAxis } from 'recharts'

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/card'
import {
  ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from '@/components/chart'
import { Currency, formatNumber } from '@/utils/loan-calculator'

import clsx from 'clsx'
import { MousePointerClick } from 'lucide-react'

// Define the payment data structure
type MonthlyPayment = {
  month: number
  netAmount: number
  vatAmount: number
  grossAmount: number
  interestAmount: number
  monthlyPayment: number
}

// Props for the component
interface LoanDebtChartProps {
  payments: MonthlyPayment[]
  currency: Currency
  calculationKey?: number // A value that changes when calculation happens
}

const chartConfig = {
  principalBalance: {
    label: 'Principal Balance',
    color: 'text-blue-600',
  },
  principal: {
    label: 'Principal Payment',
    color: 'text-violet-700',
  },
  interest: {
    label: 'Interest Payment',
    color: 'text-orange-500',
  },
} satisfies ChartConfig

export function LoanDebtChart({
  payments,
  currency,
  calculationKey = 0,
}: LoanDebtChartProps) {
  const [activeChart, setActiveChart] =
    React.useState<keyof typeof chartConfig>('principalBalance')
  const [viewMode, setViewMode] = React.useState<'month' | 'year'>('month')

  // Selected bar state with period and values information
  const [selectedBar, setSelectedBar] = React.useState<{
    period: number
    principalBalance: number
    principal: number
    interest: number
  } | null>(null)

  // Reset selectedBar when calculationKey changes (handleCalculate is called)
  React.useEffect(() => {
    setSelectedBar(null)
  }, [calculationKey])

  // Check if the loan is longer than 2 years
  const isLongTermLoan = React.useMemo(() => {
    const maxMonth = Math.max(
      ...payments
        .filter((p) => typeof p.month === 'number')
        .map((p) => p.month),
    )

    if (maxMonth <= 24) {
      setViewMode('month')
    } else {
      setViewMode('year')
    }

    return maxMonth > 24 // More than 2 years
  }, [payments])

  // Transform the payment data for the chart
  const chartData = React.useMemo(() => {
    // Filter out initial payment (month 0) and final payment (if it's a special payment)
    const regularPayments = payments.filter(
      (payment) =>
        typeof payment.month === 'number' &&
        payment.month > 0 &&
        payment.month <=
          Math.max(
            ...payments
              .filter((p) => typeof p.month === 'number')
              .map((p) => p.month),
          ),
    )

    // Calculate the initial loan balance (sum of all principal payments)
    const initialLoanBalance = regularPayments.reduce(
      (acc, curr) => acc + curr.grossAmount,
      0,
    )

    // Create base data for month 0 to show the initial balance
    const monthlyData = [
      {
        month: 0,
        year: 0,
        period: 0,
        principalBalance: initialLoanBalance,
        principal: 0,
        interest: 0,
      },
    ]

    let principalBalance = initialLoanBalance

    // Create the monthly chart data
    regularPayments.forEach((payment) => {
      const principal = payment.grossAmount
      principalBalance -= principal

      // Ensure principal balance is never negative
      const displayPrincipalBalance = Math.max(0, principalBalance)

      monthlyData.push({
        month: payment.month,
        year: Math.ceil(payment.month / 12),
        period: payment.month,
        principalBalance: displayPrincipalBalance,
        principal: principal,
        interest: payment.interestAmount,
      })
    })

    // If view mode is yearly, aggregate the data by year
    if (viewMode === 'year') {
      const yearlyData = [
        {
          period: 0,
          principalBalance: initialLoanBalance,
          principal: 0,
          interest: 0,
        },
      ]

      // Group payments by year and sum up the values
      const yearGroups = new Map()

      regularPayments.forEach((payment) => {
        const year = Math.ceil(payment.month / 12)
        if (!yearGroups.has(year)) {
          yearGroups.set(year, {
            principal: 0,
            interest: 0,
            lastMonth: 0,
          })
        }

        const group = yearGroups.get(year)
        group.principal += payment.grossAmount
        group.interest += payment.interestAmount
        group.lastMonth = Math.max(group.lastMonth, payment.month)
      })

      // Convert the grouped data to an array
      let yearlyPrincipalBalance = initialLoanBalance

      // Sort the year groups by year number
      const sortedYears = Array.from(yearGroups.keys()).sort((a, b) => a - b)

      sortedYears.forEach((year) => {
        const group = yearGroups.get(year)
        yearlyPrincipalBalance -= group.principal

        // Ensure principal balance is never negative
        const displayPrincipalBalance = Math.max(0, yearlyPrincipalBalance)

        yearlyData.push({
          period: year,
          principalBalance: displayPrincipalBalance,
          principal: group.principal,
          interest: group.interest,
        })
      })

      return yearlyData
    }

    return monthlyData
  }, [payments, viewMode])

  // Calculate totals and selected period values
  const total = React.useMemo(() => {
    const regularPayments = payments.filter(
      (payment) => typeof payment.month === 'number' && payment.month > 0,
    )

    // Default totals (for the entire loan)
    const totals = {
      principalBalance: 0, // Final principal balance is always 0
      principal: regularPayments.reduce(
        (acc, curr) => acc + curr.grossAmount,
        0,
      ),
      interest: regularPayments.reduce(
        (acc, curr) => acc + curr.interestAmount,
        0,
      ),
    }

    // If a bar is selected, use its data
    if (selectedBar !== null) {
      const selectedValues = {
        principalBalance: selectedBar.principalBalance,
        principal: selectedBar.principal,
        interest: selectedBar.interest,
      }
      return selectedValues
    }

    return totals
  }, [payments, selectedBar])

  return (
    <Card>
      <CardHeader className="flex flex-col items-stretch space-y-0 border-b p-0 lg:flex-row">
        <div className="flex flex-1 flex-col justify-evenly gap-2 px-6 py-5 sm:py-6 lg:gap-1">
          <div className="flex flex-col justify-between gap-y-2 xl:flex-row xl:items-center">
            <CardTitle>Loan Repayment Visualization</CardTitle>

            {/* View mode toggle - only show if loan is longer than 2 years */}
            {isLongTermLoan && (
              <div className="inline-flex w-fit items-center rounded-md border border-gray-200 p-1 text-sm">
                <button
                  className={`rounded-sm px-3 py-1 ${
                    viewMode === 'month' ? 'bg-gray-100 font-medium' : ''
                  }`}
                  onClick={() => {
                    setViewMode('month')
                    setSelectedBar(null)
                  }}
                >
                  Monthly
                </button>
                <button
                  className={`rounded-sm px-3 py-1 ${
                    viewMode === 'year' ? 'bg-gray-100 font-medium' : ''
                  }`}
                  onClick={() => {
                    setViewMode('year')
                    setSelectedBar(null)
                  }}
                >
                  Yearly
                </button>
              </div>
            )}
          </div>
          <CardDescription>
            {selectedBar !== null ? (
              <span>
                Showing details for{' '}
                {selectedBar.period === 0
                  ? 'Start'
                  : viewMode === 'month'
                    ? `Month ${selectedBar.period}`
                    : `Year ${selectedBar.period}`}{' '}
                <button
                  onClick={() => setSelectedBar(null)}
                  className="ml-2 text-xs text-blue-600 hover:underline"
                >
                  (View All)
                </button>
              </span>
            ) : (
              <span>
                Showing {viewMode === 'month' ? 'monthly' : 'yearly'} loan
                balance and payments
              </span>
            )}
          </CardDescription>
        </div>
        <div className="flex">
          {(['principalBalance', 'principal', 'interest'] as const).map(
            (key) => {
              // Get the correct value based on whether a bar is selected
              const displayValue = selectedBar ? selectedBar[key] : total[key]

              return (
                <button
                  key={key}
                  data-active={activeChart === key}
                  className={clsx(
                    'group relative z-30 flex flex-1 flex-col justify-center gap-1 border-t px-6 py-4 text-left transition delay-75 ease-in-out even:border-l hover:bg-gray-200 sm:border-l sm:border-t-0 sm:px-8 sm:py-6',
                    activeChart === key ? 'bg-gray-100' : 'border-gray-200',
                  )}
                  onClick={() => setActiveChart(key)}
                >
                  <span className="text-muted-foreground text-xs">
                    {chartConfig[key].label}
                  </span>
                  <span className="text-lg font-bold leading-none sm:text-3xl lg:min-w-[150px]">
                    {formatNumber(currency, displayValue)}
                  </span>
                  {selectedBar && (
                    <span className="text-xs text-blue-600">
                      {key === 'principalBalance' && selectedBar.period === 0
                        ? '(Starting balance)'
                        : key === 'principalBalance'
                          ? '(Balance after payment)'
                          : '(This period)'}
                    </span>
                  )}
                  <MousePointerClick className="absolute right-2 top-2 size-4 transition delay-75 ease-in-out group-hover:text-blue-500" />
                </button>
              )
            },
          )}
        </div>
      </CardHeader>
      <CardContent className="px-2 sm:p-6">
        {!selectedBar && (
          <div className="text-muted-foreground mb-3 text-xs">
            Click on any bar to view details for that period
          </div>
        )}
        <ChartContainer
          config={chartConfig}
          className="aspect-auto h-[350px] w-full"
        >
          <BarChart
            accessibilityLayer
            data={chartData}
            margin={{
              left: 12,
              right: 12,
              top: 20,
              bottom: 10,
            }}
          >
            <CartesianGrid vertical={false} />
            <XAxis
              dataKey="period"
              tickLine={false}
              axisLine={false}
              tickMargin={8}
              minTickGap={32}
              tickFormatter={(value) => {
                if (value === 0) return 'Start'
                return viewMode === 'month' ? `Month ${value}` : `Year ${value}`
              }}
            />
            <YAxis
              domain={[0, 'auto']} // Ensure the Y-axis starts at 0
              allowDecimals={false} // Avoid decimal values on Y-axis
              tickFormatter={(value) => {
                // Format large numbers with K, M, etc.
                if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`
                if (value >= 1000) return `${(value / 1000).toFixed(0)}K`
                return value.toString()
              }}
              tickLine={false}
              axisLine={false}
              tickMargin={8}
            />
            <ChartTooltip
              content={
                <ChartTooltipContent
                  className="w-[220px]"
                  nameKey={activeChart}
                  formatter={(value) => {
                    return formatNumber(currency, Number(value))
                  }}
                  labelFormatter={(value) => {
                    if (value === 0) return 'Starting Balance'
                    return viewMode === 'month'
                      ? `Month ${value}`
                      : `Year ${value}`
                  }}
                />
              }
            />
            <Bar
              dataKey={activeChart}
              fill="currentColor"
              className={chartConfig[activeChart].color}
              radius={[4, 4, 0, 0]}
              cursor="pointer"
              shape={(props: any) => {
                const { x, y, width, height, fill, payload, className } = props
                const isSelected =
                  selectedBar && payload.period === selectedBar.period

                return (
                  <rect
                    x={x}
                    y={y}
                    width={width}
                    height={height}
                    fill={fill}
                    className={clsx(isSelected ? 'text-black' : className)}
                    // stroke={isSelected ? '#333' : 'none'}
                    // strokeWidth={isSelected ? 1 : 0}
                    // fillOpacity={isSelected ? 0.85 : 1}
                    rx={4}
                    ry={4}
                  />
                )
              }}
              onClick={(data, index, event) => {
                if (data && data.payload) {
                  const payload = data.payload
                  const newSelectedBar = {
                    period: payload.period,
                    principalBalance: payload.principalBalance,
                    principal: payload.principal,
                    interest: payload.interest,
                  }
                  if (selectedBar?.period == newSelectedBar.period) {
                    setSelectedBar(null)
                    return
                  }
                  setSelectedBar(newSelectedBar)
                }
              }}
            />
          </BarChart>
        </ChartContainer>
      </CardContent>
    </Card>
  )
}
